import React, { useState, useEffect, useRef } from 'react';
import { Upload, RefreshCw, Database, TrendingUp, AlertCircle, CheckCircle2, FileSpreadsheet, Trash2, ShieldAlert, ShieldCheck, Link } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { Broker } from '@shared/index';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Brokers that have symbol mapping support (subset of all brokers)
const BROKERS_WITH_SYMBOL_MAPPING = [Broker.ICICI_DIRECT] as const;

interface UploadResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    exchange: string;
    tradingDate: string;
    dateSource?: string;
    filename: string;
    totalRecords: number;
    currentPrices?: {
      upserted: number;
      modified: number;
      errors: number;
    };
    priceHistory?: {
      upserted: number;
      modified: number;
      errors: number;
    };
    // Legacy fields for backwards compatibility
    upserted?: number;
    modified?: number;
    errors?: number;
  };
}

interface BrokerSymbolUploadResult {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    processed: number;
    inserted: number;
    updated: number;
    errors: number;
    broker: string;
    exchange: string;
    filename: string;
  };
}

interface BrokerSymbolStats {
  totalMappings: number;
  brokers: Array<{
    broker: string;
    total: number;
    exchanges: Array<{
      exchange: string;
      count: number;
    }>;
  }>;
}

interface StockPriceStats {
  totalDocuments: number;
  uniqueSecurities: number;
  dateRange: {
    earliest: string | null;
    latest: string | null;
  };
  countByExchange?: Array<{ exchange: string; count: number }>;
}

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<StockPriceStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [nseUploadResult, setNseUploadResult] = useState<UploadResult | null>(null);
  const [bseUploadResult, setBseUploadResult] = useState<UploadResult | null>(null);
  const [isUploadingNse, setIsUploadingNse] = useState(false);
  const [isUploadingBse, setIsUploadingBse] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isClearingPriceHistory, setIsClearingPriceHistory] = useState(false);
  const [isClearingCurrentPrices, setIsClearingCurrentPrices] = useState(false);
  const [clearResult, setClearResult] = useState<{ success: boolean; message: string } | null>(null);
  
  // Multi-file upload state
  const [multiUploadFiles, setMultiUploadFiles] = useState<File[]>([]);
  const [multiUploadExchange, setMultiUploadExchange] = useState<'NSE' | 'BSE'>('NSE');
  const [multiUploadResults, setMultiUploadResults] = useState<Array<{filename: string; result: UploadResult}>>([]);
  const [isMultiUploading, setIsMultiUploading] = useState(false);
  const [multiUploadProgress, setMultiUploadProgress] = useState<{current: number; total: number; currentFile: string}>({current: 0, total: 0, currentFile: ''});
  
  const nseFileInputRef = useRef<HTMLInputElement>(null);
  const bseFileInputRef = useRef<HTMLInputElement>(null);
  const multiFileInputRef = useRef<HTMLInputElement>(null);
  const brokerSymbolFileInputRef = useRef<HTMLInputElement>(null);
  
  // Broker symbol mapping state
  const [selectedBroker, setSelectedBroker] = useState<Broker>(Broker.ICICI_DIRECT);
  const [brokerSymbolFile, setBrokerSymbolFile] = useState<File | null>(null);
  const [isUploadingBrokerSymbols, setIsUploadingBrokerSymbols] = useState(false);
  const [brokerSymbolUploadResult, setBrokerSymbolUploadResult] = useState<BrokerSymbolUploadResult | null>(null);
  const [brokerSymbolStats, setBrokerSymbolStats] = useState<BrokerSymbolStats | null>(null);
  const [isLoadingBrokerStats, setIsLoadingBrokerStats] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    fetchStats();
    fetchBrokerSymbolStats();
  }, []);

  const checkAdminAccess = async () => {
    setIsCheckingAdmin(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/verify`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setIsAdmin(data.data?.isAdmin || false);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stock-prices/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchBrokerSymbolStats = async () => {
    setIsLoadingBrokerStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/broker-symbols/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBrokerSymbolStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching broker symbol stats:', error);
    } finally {
      setIsLoadingBrokerStats(false);
    }
  };

  const handleBrokerSymbolFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setBrokerSymbolFile(file);
      setBrokerSymbolUploadResult(null);
    }
  };

  const handleBrokerSymbolUpload = async () => {
    if (!brokerSymbolFile) return;
    
    setIsUploadingBrokerSymbols(true);
    setBrokerSymbolUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', brokerSymbolFile);
      formData.append('broker', selectedBroker);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/broker-symbols/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      const data = await response.json();
      setBrokerSymbolUploadResult(data);
      
      if (data.success) {
        await fetchBrokerSymbolStats();
        setBrokerSymbolFile(null);
        if (brokerSymbolFileInputRef.current) {
          brokerSymbolFileInputRef.current.value = '';
        }
      }
    } catch (error) {
      setBrokerSymbolUploadResult({
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      });
    } finally {
      setIsUploadingBrokerSymbols(false);
    }
  };

  const clearBrokerSymbolUpload = () => {
    setBrokerSymbolFile(null);
    setBrokerSymbolUploadResult(null);
    if (brokerSymbolFileInputRef.current) {
      brokerSymbolFileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (
    file: File,
    exchange: 'NSE' | 'BSE',
    setResult: (result: UploadResult | null) => void,
    setIsUploading: (loading: boolean) => void
  ) => {
    setIsUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_BASE_URL}/api/stock-prices/upload?exchange=${exchange}`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      const data = await response.json();
      setResult(data);

      // Refresh stats after successful upload
      if (data.success) {
        await fetchStats();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleNseFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'NSE', setNseUploadResult, setIsUploadingNse);
    }
    // Reset input
    if (nseFileInputRef.current) {
      nseFileInputRef.current.value = '';
    }
  };

  const handleBseFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'BSE', setBseUploadResult, setIsUploadingBse);
    }
    // Reset input
    if (bseFileInputRef.current) {
      bseFileInputRef.current.value = '';
    }
  };

  const clearUploadResult = (exchange: 'NSE' | 'BSE') => {
    if (exchange === 'NSE') {
      setNseUploadResult(null);
    } else {
      setBseUploadResult(null);
    }
  };

  const handleClearPriceHistory = async () => {
    if (!window.confirm('Are you sure you want to delete ALL price history data? This action cannot be undone.')) {
      return;
    }
    
    setIsClearingPriceHistory(true);
    setClearResult(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/clear-price-history`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setClearResult({ success: true, message: data.message || `Deleted ${data.data?.deleted || 0} documents` });
        await fetchStats();
      } else {
        setClearResult({ success: false, message: data.error?.message || 'Failed to clear price history' });
      }
    } catch (error) {
      setClearResult({ success: false, message: error instanceof Error ? error.message : 'Failed to clear price history' });
    } finally {
      setIsClearingPriceHistory(false);
    }
  };

  const handleClearCurrentPrices = async () => {
    if (!window.confirm('Are you sure you want to delete ALL current stock prices? This action cannot be undone.')) {
      return;
    }
    
    setIsClearingCurrentPrices(true);
    setClearResult(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/clear-current-prices`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setClearResult({ success: true, message: data.message || `Deleted ${data.data?.deleted || 0} documents` });
        await fetchStats();
      } else {
        setClearResult({ success: false, message: data.error?.message || 'Failed to clear current prices' });
      }
    } catch (error) {
      setClearResult({ success: false, message: error instanceof Error ? error.message : 'Failed to clear current prices' });
    } finally {
      setIsClearingCurrentPrices(false);
    }
  };

  // Multi-file upload handlers
  const handleMultiFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setMultiUploadFiles(Array.from(files));
      setMultiUploadResults([]);
    }
  };

  const handleMultiFileUpload = async () => {
    if (multiUploadFiles.length === 0) return;
    
    setIsMultiUploading(true);
    setMultiUploadResults([]);
    setMultiUploadProgress({ current: 0, total: multiUploadFiles.length, currentFile: '' });
    
    const results: Array<{filename: string; result: UploadResult}> = [];
    
    for (let i = 0; i < multiUploadFiles.length; i++) {
      const file = multiUploadFiles[i];
      setMultiUploadProgress({ current: i + 1, total: multiUploadFiles.length, currentFile: file.name });
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_BASE_URL}/api/stock-prices/upload?exchange=${multiUploadExchange}`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        
        const data = await response.json();
        results.push({ filename: file.name, result: data });
      } catch (error) {
        results.push({
          filename: file.name,
          result: {
            success: false,
            error: error instanceof Error ? error.message : 'Upload failed'
          }
        });
      }
      
      // Update results after each file
      setMultiUploadResults([...results]);
    }
    
    setIsMultiUploading(false);
    setMultiUploadFiles([]);
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
    }
    
    // Refresh stats after all uploads
    await fetchStats();
  };

  const clearMultiUploadResults = () => {
    setMultiUploadResults([]);
    setMultiUploadFiles([]);
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const UploadResultAlert: React.FC<{ result: UploadResult; exchange: 'NSE' | 'BSE' }> = ({ result, exchange }) => {
    if (result.success) {
      const currentPrices = result.data?.currentPrices;
      const priceHistory = result.data?.priceHistory;
      // Support both old and new response format
      const upserted = currentPrices?.upserted ?? result.data?.upserted ?? 0;
      const modified = currentPrices?.modified ?? result.data?.modified ?? 0;
      const errors = currentPrices?.errors ?? result.data?.errors ?? 0;
      
      return (
        <Alert className="mt-4 border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{result.message}</p>
                {result.data && (
                  <div className="mt-2 text-sm space-y-1">
                    <p>📅 Trading Date: {formatDate(result.data.tradingDate)} {result.data.dateSource === 'csv' ? '(from CSV)' : ''}</p>
                    <p>📊 Total Records: {result.data.totalRecords.toLocaleString()}</p>
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <p className="font-medium text-green-700">Current Prices:</p>
                      <p>➕ New: {upserted.toLocaleString()}</p>
                      <p>🔄 Updated: {modified.toLocaleString()}</p>
                      {errors > 0 && (
                        <p className="text-amber-600">⚠️ Errors: {errors}</p>
                      )}
                    </div>
                    {priceHistory && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <p className="font-medium text-green-700">Price History:</p>
                        <p>➕ New: {priceHistory.upserted.toLocaleString()}</p>
                        <p>🔄 Updated: {priceHistory.modified.toLocaleString()}</p>
                        {priceHistory.errors > 0 && (
                          <p className="text-amber-600">⚠️ Errors: {priceHistory.errors}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => clearUploadResult(exchange)}
                className="text-green-600 hover:text-green-800"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert className="mt-4 border-red-200 bg-red-50">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium">Upload Failed</p>
              <p className="text-sm mt-1">{result.error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearUploadResult(exchange)}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs text-gray-500">Stock Price Management</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStats}
              disabled={isLoadingStats}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingStats ? 'animate-spin' : ''}`} />
              Refresh Stats
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Access Status */}
        {isCheckingAdmin ? (
          <div className="mb-6 flex items-center gap-2 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Checking admin access...
          </div>
        ) : isAdmin ? (
          <div className="mb-6">
            <Alert className="border-green-200 bg-green-50">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <span className="font-medium">Admin Access Granted</span> - You have full administrative privileges ({user?.email})
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="mb-6">
            <Alert className="border-amber-200 bg-amber-50">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <span className="font-medium">Limited Access</span> - Admin features are restricted. Contact admin for access.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Admin Controls - Only visible to admins */}
        {isAdmin && (
          <Card className="mb-8 border-red-200 bg-red-50/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-red-900">Danger Zone</CardTitle>
                  <CardDescription className="text-red-700">Administrative actions - Use with caution</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-red-200 rounded-lg bg-white">
                  <h4 className="font-medium text-gray-900 mb-2">Clear Price History</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Delete all documents from the stock_price_history collection. This removes all historical price data used for daily/weekly/monthly change calculations.
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClearPriceHistory}
                    disabled={isClearingPriceHistory}
                  >
                    {isClearingPriceHistory ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Price History
                      </>
                    )}
                  </Button>
                </div>

                <div className="p-4 border border-red-200 rounded-lg bg-white">
                  <h4 className="font-medium text-gray-900 mb-2">Clear Current Prices</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Delete all documents from the stock_prices collection. This removes all current stock price data.
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClearCurrentPrices}
                    disabled={isClearingCurrentPrices}
                  >
                    {isClearingCurrentPrices ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Current Prices
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {clearResult && (
                <Alert className={`mt-4 ${clearResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  {clearResult.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription className={clearResult.success ? 'text-green-800' : 'text-red-800'}>
                    {clearResult.message}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Overview - Price History Only */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Documents</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {isLoadingStats ? '...' : stats?.totalDocuments?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unique Securities</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {isLoadingStats ? '...' : stats?.uniqueSecurities?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Earliest Date</p>
                  <p className="text-lg font-bold text-gray-900">
                    {isLoadingStats ? '...' : formatDate(stats?.dateRange?.earliest || null)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Latest Date</p>
                  <p className="text-lg font-bold text-gray-900">
                    {isLoadingStats ? '...' : formatDate(stats?.dateRange?.latest || null)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Exchange Breakdown */}
        {stats?.countByExchange && stats.countByExchange.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {stats.countByExchange.map((ex) => (
              <Card key={ex.exchange} className="bg-gradient-to-br from-indigo-50 to-white">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Database className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{ex.exchange} Records</p>
                      <p className="text-2xl font-bold text-indigo-900">
                        {ex.count?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* NSE BhavCopy Upload */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Upload className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle>NSE BhavCopy</CardTitle>
                  <CardDescription>Upload daily NSE equity bhavcopy CSV</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-green-400 hover:bg-green-50 transition-colors">
                  <input
                    ref={nseFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleNseFileChange}
                    className="hidden"
                    id="nse-file-input"
                  />
                  <label htmlFor="nse-file-input" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {isUploadingNse ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        <>
                          <span className="text-green-600 font-medium">Click to upload</span> or drag and drop
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">CSV file from NSE BhavCopy</p>
                  </label>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p className="font-medium">Expected columns:</p>
                  <p>SYMBOL, SERIES, OPEN, HIGH, LOW, CLOSE, ISIN, ...</p>
                  <p className="text-gray-400">File naming: 20251212_NSE.csv (date auto-detected)</p>
                </div>

                {nseUploadResult && <UploadResultAlert result={nseUploadResult} exchange="NSE" />}
              </div>
            </CardContent>
          </Card>

          {/* BSE BhavCopy Upload */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Upload className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>BSE BhavCopy</CardTitle>
                  <CardDescription>Upload daily BSE equity bhavcopy CSV</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <input
                    ref={bseFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleBseFileChange}
                    className="hidden"
                    id="bse-file-input"
                  />
                  <label htmlFor="bse-file-input" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {isUploadingBse ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        <>
                          <span className="text-purple-600 font-medium">Click to upload</span> or drag and drop
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">CSV file from BSE BhavCopy</p>
                  </label>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p className="font-medium">Expected columns:</p>
                  <p>TckrSymb, ISIN, FinInstrmId, OpnPric, HghPric, LwPric, ClsPric, ...</p>
                  <p className="text-gray-400">File naming: BhavCopy_BSE_*.csv (date auto-detected)</p>
                </div>

                {bseUploadResult && <UploadResultAlert result={bseUploadResult} exchange="BSE" />}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Multi-File Batch Upload */}
        <Card className="mt-8 border-indigo-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle>Batch Upload (Multiple Files)</CardTitle>
                <CardDescription>Upload multiple BhavCopy files and process them one by one</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Exchange Selection */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Exchange:</span>
                <div className="flex gap-2">
                  <Button
                    variant={multiUploadExchange === 'NSE' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMultiUploadExchange('NSE')}
                    disabled={isMultiUploading}
                    className={multiUploadExchange === 'NSE' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    NSE
                  </Button>
                  <Button
                    variant={multiUploadExchange === 'BSE' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMultiUploadExchange('BSE')}
                    disabled={isMultiUploading}
                    className={multiUploadExchange === 'BSE' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    BSE
                  </Button>
                </div>
              </div>

              {/* File Selection */}
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                <input
                  ref={multiFileInputRef}
                  type="file"
                  accept=".csv"
                  multiple
                  onChange={handleMultiFileSelect}
                  className="hidden"
                  id="multi-file-input"
                  disabled={isMultiUploading}
                />
                <label htmlFor="multi-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    <span className="text-indigo-600 font-medium">Click to select multiple files</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Select multiple CSV files at once</p>
                </label>
              </div>

              {/* Selected Files List */}
              {multiUploadFiles.length > 0 && !isMultiUploading && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {multiUploadFiles.length} file(s) selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setMultiUploadFiles([]);
                        if (multiFileInputRef.current) multiFileInputRef.current.value = '';
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {multiUploadFiles.map((file, idx) => (
                      <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                        <FileSpreadsheet className="w-3 h-3" />
                        {file.name}
                      </div>
                    ))}
                  </div>
                  <Button
                    className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleMultiFileUpload}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload All Files ({multiUploadExchange})
                  </Button>
                </div>
              )}

              {/* Upload Progress */}
              {isMultiUploading && (
                <div className="bg-indigo-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm font-medium text-indigo-900">
                      Processing file {multiUploadProgress.current} of {multiUploadProgress.total}
                    </span>
                  </div>
                  <div className="text-xs text-indigo-700 mb-2">
                    Current: {multiUploadProgress.currentFile}
                  </div>
                  <div className="w-full bg-indigo-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(multiUploadProgress.current / multiUploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Upload Results */}
              {multiUploadResults.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Upload Results</span>
                    <Button variant="ghost" size="sm" onClick={clearMultiUploadResults}>
                      <Trash2 className="w-4 h-4 mr-1" />
                      Clear Results
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {multiUploadResults.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          item.result.success
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {item.result.success ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${item.result.success ? 'text-green-900' : 'text-red-900'}`}>
                              {item.filename}
                            </p>
                            {item.result.success && item.result.data && (
                              <p className="text-xs text-green-700">
                                {item.result.data.totalRecords} records • 
                                History: +{item.result.data.priceHistory?.upserted || 0} / 
                                ~{item.result.data.priceHistory?.modified || 0}
                                {(item.result.data.priceHistory?.errors || 0) > 0 && (
                                  <span className="text-amber-600"> / {item.result.data.priceHistory?.errors} errors</span>
                                )}
                              </p>
                            )}
                            {!item.result.success && (
                              <p className="text-xs text-red-700">{item.result.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-gray-100 rounded-lg p-3 text-sm">
                    <div className="flex justify-between">
                      <span>Total Files:</span>
                      <span className="font-medium">{multiUploadResults.length}</span>
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span>Successful:</span>
                      <span className="font-medium">{multiUploadResults.filter(r => r.result.success).length}</span>
                    </div>
                    <div className="flex justify-between text-red-700">
                      <span>Failed:</span>
                      <span className="font-medium">{multiUploadResults.filter(r => !r.result.success).length}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Broker Symbol Mapping Section - Admin Only */}
        {isAdmin && (
          <Card className="mt-8 border-cyan-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Link className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <CardTitle>Broker Symbol Mappings</CardTitle>
                  <CardDescription>Upload broker master files to map symbols to ISINs (for brokers without ISIN in tradebooks)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Stats */}
                {brokerSymbolStats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-cyan-50 rounded-lg p-4">
                      <p className="text-sm text-cyan-600 font-medium">Total Mappings</p>
                      <p className="text-2xl font-bold text-cyan-900">
                        {isLoadingBrokerStats ? '...' : brokerSymbolStats.totalMappings.toLocaleString()}
                      </p>
                    </div>
                    {brokerSymbolStats.brokers.map((b) => (
                      <div key={b.broker} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 font-medium">{b.broker}</p>
                        <p className="text-xl font-bold text-gray-900">{b.total.toLocaleString()}</p>
                        <div className="text-xs text-gray-500 mt-1">
                          {b.exchanges.map(e => `${e.exchange}: ${e.count.toLocaleString()}`).join(' | ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Form */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700">Broker:</span>
                    <div className="flex gap-2">
                      {BROKERS_WITH_SYMBOL_MAPPING.map((broker) => (
                        <Button
                          key={broker}
                          variant={selectedBroker === broker ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedBroker(broker)}
                          disabled={isUploadingBrokerSymbols}
                          className={selectedBroker === broker ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                        >
                          {broker}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* File Selection */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-cyan-400 hover:bg-cyan-50 transition-colors">
                    <input
                      ref={brokerSymbolFileInputRef}
                      type="file"
                      accept=".txt,.csv"
                      onChange={handleBrokerSymbolFileSelect}
                      className="hidden"
                      id="broker-symbol-file-input"
                      disabled={isUploadingBrokerSymbols}
                    />
                    <label htmlFor="broker-symbol-file-input" className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        <span className="text-cyan-600 font-medium">Click to select master file</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Exchange is detected from filename (e.g., NSEScripMaster.txt, BSEScripMaster.txt)
                      </p>
                    </label>
                  </div>

                  {/* Selected File & Upload Button */}
                  {brokerSymbolFile && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">{brokerSymbolFile.name}</span>
                          <span className="text-xs text-gray-400">
                            ({(brokerSymbolFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={clearBrokerSymbolUpload}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        className="w-full bg-cyan-600 hover:bg-cyan-700"
                        onClick={handleBrokerSymbolUpload}
                        disabled={isUploadingBrokerSymbols}
                      >
                        {isUploadingBrokerSymbols ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {selectedBroker} Master File
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Upload Result */}
                  {brokerSymbolUploadResult && (
                    <Alert className={`${brokerSymbolUploadResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      {brokerSymbolUploadResult.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <AlertDescription className={brokerSymbolUploadResult.success ? 'text-green-800' : 'text-red-800'}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{brokerSymbolUploadResult.message || (brokerSymbolUploadResult.success ? 'Upload successful' : 'Upload failed')}</p>
                            {brokerSymbolUploadResult.success && brokerSymbolUploadResult.data && (
                              <div className="text-sm mt-1 space-y-0.5">
                                <p>📁 {brokerSymbolUploadResult.data.filename} ({brokerSymbolUploadResult.data.broker} {brokerSymbolUploadResult.data.exchange})</p>
                                <p>📊 Processed: {brokerSymbolUploadResult.data.processed.toLocaleString()}</p>
                                <p>➕ Inserted: {brokerSymbolUploadResult.data.inserted.toLocaleString()}</p>
                                <p>🔄 Updated: {brokerSymbolUploadResult.data.updated.toLocaleString()}</p>
                                {brokerSymbolUploadResult.data.errors > 0 && (
                                  <p className="text-amber-600">⚠️ Errors: {brokerSymbolUploadResult.data.errors}</p>
                                )}
                              </div>
                            )}
                            {!brokerSymbolUploadResult.success && brokerSymbolUploadResult.error && (
                              <p className="text-sm mt-1">{brokerSymbolUploadResult.error}</p>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setBrokerSymbolUploadResult(null)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Help Text */}
                  <div className="text-xs text-gray-500 space-y-1 pt-2 border-t">
                    <p className="font-medium">How it works:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Upload broker master files (e.g., ICICI's NSEScripMaster.txt, BSEScripMaster.txt)</li>
                      <li>The system extracts Symbol → ISIN mappings</li>
                      <li>During tradebook imports, symbols without ISIN are resolved using these mappings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">📋 How to get BhavCopy files</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">NSE BhavCopy</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                  <li>Go to NSE India website</li>
                  <li>Navigate to Market Data → Historical Data</li>
                  <li>Select "Equity" and download CSV</li>
                  <li>Or use Samco/broker portal for bhavcopy</li>
                </ol>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">BSE BhavCopy</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-600">
                  <li>Go to BSE India website</li>
                  <li>Navigate to Market Statistics → Bhavcopy</li>
                  <li>Download the CM (Cash Market) CSV</li>
                  <li>Or use Samco/broker portal for bhavcopy</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPage;
